export const translations = {
  en: {
    nav: {
      services: "Services",
      cases: "Use cases",
      pricing: "Pricing",
      faq: "FAQ",
      about: "About",
      cta: "Start your project",
    },
    hero: {
      eyebrow: "Morocco · Africa · Worldwide · Est. 2026",
      title_pre: "Transforming ideas into",
      title_accent: "AI-powered",
      title_post: "reality",
      subtitle:
        "We design, build and deploy AI agents, apps and websites for ambitious businesses in Morocco and Africa. No templates. No shortcuts. Just results.",
      primary: "Start your project",
      secondary: "Talk on WhatsApp",
      micro: "Free 30-min discovery call · No commitment",
      live: {
        label: "Live",
        value: "+218 h",
        caption: "automated this month across our agents",
        responseLabel: "Avg. response",
        responseValue: "1.4s",
      },
    },
    proof: {
      label: "Numbers that move the needle",
      items: [
        { value: "7", label: "Products shipped" },
        { value: "20+ h", label: "Saved per week" },
        { value: "47%", label: "AI cost cut · Router" },
        { value: "$4.2k", label: "Avg. monthly savings" },
        { value: "100%", label: "Custom builds" },
      ],
      strip: "LABMIND AI · AGENTFLOW · AI ROUTER · CLOUDOPS · COMMERCEAI · INDUSTRYMIND",
    },
    ps: {
      eyebrow: "The shift",
      title: "Operations weren't built for 2026.",
      problems: {
        title: "Where teams bleed time",
        items: [
          "Manual reagent and stock tracking",
          "Customer support backlogs in EN / FR / AR",
          "Fragmented tools — CRM, WhatsApp, email, sheets",
          "Decisions delayed by missing real-time data",
        ],
      },
      solutions: {
        title: "What we install",
        items: [
          "AI agents that act, not just chat",
          "HL7 / ASTM real-time analyzer integrations",
          "End-to-end automation across your stack",
          "Sector-tuned models for African realities",
        ],
      },
    },
    services: {
      eyebrow: "What we build",
      title: "End-to-end AI solutions, ready to grow with you.",
      items: [
        {
          tag: "01 · Flagship",
          icon: "lab",
          title: "LabMind AI",
          desc: "Real-time reagent inventory for medical laboratories. Connected to analyzers via HL7/ASTM. Predicts stockouts 7 days ahead. ISO 15189 compliant.",
          bullets: ["HL7 / ASTM", "ISO 15189", "FEFO · Auto-order"],
          status: "Live",
        },
        {
          tag: "02",
          icon: "bot",
          title: "AgentFlow",
          desc: "Custom AI agents for customer support, lead qualification and workflow automation. CRM + WhatsApp + email. 20h+ saved per week.",
          bullets: ["Customer support", "Lead qualification", "Workflow automation"],
          status: "Live",
        },
        {
          tag: "03",
          icon: "globe",
          title: "AI Websites",
          desc: "Creative, AI-integrated websites and landing pages. Premium design, mobile-first, fast. From €490 in 5–7 days.",
          bullets: ["Mobile-first", "SSL · CDN", "5–7 days"],
          status: "Live",
        },
        {
          tag: "04",
          icon: "cloud",
          title: "CloudOps",
          desc: "Server selection, CI/CD, Docker, 24/7 monitoring, SSL, DNS, CDN. AWS, GCP, Hetzner, OVH — fully managed.",
          bullets: ["CI/CD · Docker", "24/7 monitoring", "Backups + patches"],
          status: "Live",
        },
        {
          tag: "05",
          icon: "cart",
          title: "CommerceAI",
          desc: "AI-powered online commerce. Dynamic pricing, recommendations, demand forecasting. Arabic & French NLP.",
          bullets: ["Dynamic pricing", "Recommendations", "AR · FR NLP"],
          status: "Live",
        },
        {
          tag: "06",
          icon: "factory",
          title: "IndustryMind",
          desc: "Predictive maintenance for manufacturers. OPC-UA / SCADA integration. Prevent breakdowns before they happen.",
          bullets: ["OPC-UA · SCADA", "Predictive AI", "Industry 4.0"],
          status: "Coming soon",
        },
        {
          tag: "07 · New",
          icon: "router",
          title: "AI Router",
          desc: "Cut your AI costs by 30–70%. Smart LLM routing across Claude, GPT, Gemini and local models — drop-in replacement for OpenAI-compatible endpoints, with automatic failover and per-feature budget caps.",
          bullets: ["30–70% cost cut", "Auto failover", "OpenAI-compatible"],
          status: "Live",
        },
      ],
    },
    cases: {
      eyebrow: "Use cases",
      title: "Solutions for every business stage.",
      items: [
        {
          tag: "Medical Laboratories",
          title: "LabMind AI in production",
          problem:
            "Labs spent 30+ hours per week tracking reagents manually, with frequent stockouts and missed tests across HbA1c, TSH and CRP lines.",
          solution:
            "We connected analyzers via HL7/ASTM, deployed FEFO logic and J-7 stockout prediction, and automated supplier orders — ISO 15189 compliant end to end.",
          metric: "J-7",
          metricLabel: "stockout prediction window",
        },
        {
          tag: "SMBs & E-commerce",
          title: "AgentFlow on WhatsApp",
          problem:
            "Support teams overwhelmed by orders, refund and product queries in three languages — response times slipping past 36 hours.",
          solution:
            "A custom AI agent on WhatsApp + email + CRM, trained on the catalog and policies. Live in two weeks, multilingual EN/FR/AR.",
          metric: "20+ h",
          metricLabel: "reclaimed every week",
        },
        {
          tag: "AI Startups & SaaS",
          title: "AI Router cutting LLM bills",
          problem:
            "A SaaS team was spending $9k/month on a single premium LLM provider — every query hit the same expensive model, cheap or complex.",
          solution:
            "AI Router routes each prompt to the cheapest model that meets quality, with automatic failover across Claude, GPT, Gemini and a local Llama 3. Drop-in OpenAI-compatible endpoint — zero refactor.",
          metric: "47%",
          metricLabel: "cost reduction · $4,218 saved last month",
        },
      ],
    },
    process: {
      eyebrow: "How it works",
      title: "From discovery to deployment in four moves.",
      steps: [
        { n: "01", title: "Discover", desc: "Free 30-min call. We map your workflows and surface the highest-leverage automations." },
        { n: "02", title: "Build", desc: "Agents, integrations and pipelines on your stack — observability and security baked in." },
        { n: "03", title: "Deploy", desc: "Live in 5–7 days for websites, 2–3 weeks for custom AI agents. Team trained and onboarded." },
        { n: "04", title: "Scale", desc: "Monthly optimization, new agents, new wins. Dedicated manager and SLA on Enterprise." },
      ],
    },
    testimonials: {
      eyebrow: "Client voices",
      title: "Operators who shipped — in their own words.",
      note: "Names withheld at clients' request. Industry and region kept for context.",
      items: [
        {
          quote:
            "LabMind AI eliminated our reagent stockouts. The team understood ISO 15189 from day one and shipped a tool our biologists actually use every morning.",
          role: "Lab Director",
          company: "Medical analysis laboratory · Morocco",
          initials: "LD",
        },
        {
          quote:
            "AgentFlow took our WhatsApp from chaos to autopilot. 70% of customer queries now resolved without our team even touching the inbox. Two weeks from kickoff to live.",
          role: "Founder",
          company: "E-commerce brand · Morocco",
          initials: "EC",
        },
        {
          quote:
            "We were burning $9k/month on a single LLM provider. AI Router cut that to $4.8k in the first 30 days — same quality, zero code changes. The dashboard alone is worth the subscription.",
          role: "CTO",
          company: "SaaS startup · North Africa",
          initials: "CT",
        },
        {
          quote:
            "We hired three agencies before MAIF. They were the only ones who measured success in hours saved, not in slides. The team feels like an in-house engineering partner.",
          role: "COO",
          company: "Logistics company · West Africa",
          initials: "CO",
        },
      ],
    },
    pricing: {
      eyebrow: "Investment",
      title: "Transparent pricing. No surprises.",
      toggle: { oneTime: "One-time", monthly: "Monthly · no setup fee" },
      tiers: [
        {
          name: "Starter",
          subtitle: "AI Website / Chatbot",
          price: "€490",
          priceMonthly: "€149/mo",
          cadence: "one-time",
          cadenceMonthly: "+ €99/mo · cancel anytime",
          desc: "Landing page or AI chatbot, deployed in 5–7 days.",
          features: [
            "Landing page or AI chatbot",
            "Mobile-first + SSL",
            "Deployed in 5–7 days",
            "30-day free support",
          ],
          cta: "Get started",
        },
        {
          name: "Custom AI Agent",
          subtitle: "Most popular",
          price: "€1,500",
          priceMonthly: "€399/mo",
          cadence: "up to €4,000 · one-time",
          cadenceMonthly: "+ €299/mo · no setup fee",
          desc: "Fully custom AI agent integrated into your stack.",
          features: [
            "Fully custom AI agent",
            "CRM + WhatsApp + email",
            "Delivered in 2–3 weeks",
            "3 months of support",
          ],
          cta: "Start this project",
          highlight: true,
        },
        {
          name: "Enterprise",
          subtitle: "Full AI Transformation",
          price: "€5,000",
          priceMonthly: "€899/mo",
          cadence: "up to €15,000 · one-time",
          cadenceMonthly: "+ €599/mo · full build included",
          desc: "Multiple agents, end-to-end automation, dedicated manager.",
          features: [
            "Multiple AI agents",
            "End-to-end automation",
            "Staff training + docs",
            "Dedicated manager + SLA",
          ],
          cta: "Talk to us",
        },
      ],
      cloudOps: {
        name: "CloudOps",
        subtitle: "Cloud & DevOps",
        price: "€299",
        priceMonthly: "€199/mo",
        cadence: "setup · + €149/mo managed",
        cadenceMonthly: "fully managed infra",
        features: ["Server + DNS + SSL + CDN", "CI/CD + Docker pipeline", "24/7 monitoring + alerts", "Monthly backups + patches"],
        cta: "Deploy infra",
      },
      aiRouter: {
        name: "AI Router",
        subtitle: "LLM cost optimization",
        price: "$49",
        priceMonthly: "$199/mo",
        cadence: "/mo Starter · 500K tokens",
        cadenceMonthly: "Pro · 5M tokens · unlimited providers",
        features: [
          "Smart routing across LLMs",
          "Auto failover · 99.99% uptime",
          "Budget caps + real-time alerts",
          "OpenAI-compatible · zero refactor",
        ],
        cta: "Start free trial",
      },
    },
    faq: {
      eyebrow: "Frequently asked",
      title: "Everything you wanted to ask before booking the call.",
      items: [
        {
          q: "How fast can you actually deliver?",
          a: "Landing pages and AI websites: 5–7 days. Custom AI agents (AgentFlow): 2–3 weeks from kickoff to live. Enterprise transformations: 6–10 weeks depending on scope. We commit to dates in writing before we start.",
        },
        {
          q: "Do you work with non-Moroccan clients?",
          a: "Yes. We're headquartered in Morocco and serve African enterprises and clients worldwide. We work in English, French and Arabic, and our team is comfortable with EU and US business hours.",
        },
        {
          q: "What if the AI hallucinates or makes a mistake?",
          a: "Every agent we ship has guardrails: confidence thresholds, retrieval grounding, human-in-the-loop escalations, and full audit logs. Critical workflows (medical, financial) always require human approval before action. We measure error rates and tune until they're below your tolerance.",
        },
        {
          q: "How do you handle data privacy and GDPR?",
          a: "Your data is yours. AI Router never stores prompt content or responses (metadata only, encrypted in transit). For LabMind AI we ship ISO 15189 audit trails. We sign DPAs and offer EU data residency on request. No training of external models on your data — ever.",
        },
        {
          q: "Can I cancel my monthly plan?",
          a: "Yes, anytime. Monthly subscriptions cancel at the end of the billing cycle, no questions asked. AI Router includes a 14-day free trial and a 30-day money-back guarantee if we don't cut your costs by at least 20%.",
        },
        {
          q: "What languages do your AI agents support?",
          a: "Out of the box: English, French and Arabic (including darija). For other languages we plug into Claude, GPT-4 or Gemini multilingual models — translation and transcription are included in AgentFlow and CommerceAI.",
        },
      ],
    },
    lead: {
      eyebrow: "Free discovery call",
      title: "Your next big idea deserves AI.",
      desc: "Tell us about your business. We'll send back the three highest-leverage automations — no sales pitch required.",
      name: "Full name",
      email: "Work email",
      business: "Which product interests you?",
      message: "Tell us about your operations (optional)",
      submit: "Book free call",
      submitting: "Sending…",
      success: "Request received. We'll be in touch within 24 hours.",
      error: "Something went wrong. Please try again.",
      privacy: "We respect your inbox. No spam, ever.",
    },
    about: {
      eyebrow: "Who we are",
      title: "Built in Africa for the world.",
      body:
        "Moroccan AI Factory is a Morocco-based studio serving African enterprises and clients worldwide. We design AI agents, apps and websites that move the needle — for labs, SMBs and growing teams across the continent and beyond. We measure success in hours saved and revenue captured, not in slides.",
      stats: [
        { v: "Morocco", l: "Headquarters" },
        { v: "Africa & Worldwide", l: "Reach" },
        { v: "EN · FR · AR", l: "Languages" },
      ],
      founder: "Founder",
    },
    finalCta: {
      title_pre: "Your next big idea deserves",
      title_accent: "AI",
      title_post: ".",
      desc: "We turn complex business challenges into powerful AI solutions. No templates. No shortcuts. Just results.",
      primary: "Start your project",
      secondary: "WhatsApp us",
    },
    footer: {
      tagline: "AI agents, apps and websites — built in Morocco for the world.",
      rights: "All rights reserved.",
      links: {
        product: "Products",
        company: "Company",
        legal: "Contact",
      },
    },
  },
  fr: {
    nav: {
      services: "Services",
      cases: "Cas d'usage",
      pricing: "Tarifs",
      faq: "FAQ",
      about: "À propos",
      cta: "Lancer un projet",
    },
    hero: {
      eyebrow: "Maroc · Afrique · Monde · Depuis 2026",
      title_pre: "Transformer vos idées en",
      title_accent: "réalité",
      title_post: "alimentée par l'IA",
      subtitle:
        "Nous concevons, développons et déployons des agents IA, applications et sites web pour les entreprises ambitieuses du Maroc et de l'Afrique. Pas de templates. Pas de raccourcis. Que des résultats.",
      primary: "Lancer un projet",
      secondary: "Discuter sur WhatsApp",
      micro: "Appel découverte de 30 min · Sans engagement",
      live: {
        label: "En direct",
        value: "+218 h",
        caption: "automatisées ce mois-ci par nos agents",
        responseLabel: "Réponse moy.",
        responseValue: "1,4s",
      },
    },
    proof: {
      label: "Des chiffres qui comptent",
      items: [
        { value: "7", label: "Produits livrés" },
        { value: "20+ h", label: "Économisées / semaine" },
        { value: "47%", label: "Coûts IA réduits · Router" },
        { value: "4,2k $", label: "Économies moyennes / mois" },
        { value: "100%", label: "Sur-mesure" },
      ],
      strip: "LABMIND AI · AGENTFLOW · AI ROUTER · CLOUDOPS · COMMERCEAI · INDUSTRYMIND",
    },
    ps: {
      eyebrow: "Le virage",
      title: "Vos opérations ne sont pas pensées pour 2026.",
      problems: {
        title: "Où vos équipes perdent du temps",
        items: [
          "Suivi manuel des réactifs et stocks",
          "Support client saturé en EN / FR / AR",
          "Outils fragmentés — CRM, WhatsApp, e-mail, tableurs",
          "Décisions retardées faute de données en temps réel",
        ],
      },
      solutions: {
        title: "Ce que nous installons",
        items: [
          "Des agents IA qui agissent, pas seulement qui répondent",
          "Intégrations analyseurs HL7 / ASTM en temps réel",
          "Automatisation de bout en bout sur votre stack",
          "Modèles adaptés aux réalités africaines",
        ],
      },
    },
    services: {
      eyebrow: "Ce que nous construisons",
      title: "Des solutions IA complètes, prêtes à grandir avec vous.",
      items: [
        {
          tag: "01 · Flagship",
          icon: "lab",
          title: "LabMind AI",
          desc: "Inventaire de réactifs en temps réel pour laboratoires médicaux. Connecté aux analyseurs via HL7/ASTM. Prédiction de rupture à J-7. Conforme ISO 15189.",
          bullets: ["HL7 / ASTM", "ISO 15189", "FEFO · Auto-commande"],
          status: "Live",
        },
        {
          tag: "02",
          icon: "bot",
          title: "AgentFlow",
          desc: "Agents IA sur-mesure pour le support client, la qualification commerciale et l'automatisation. CRM + WhatsApp + e-mail. 20h+ économisées / semaine.",
          bullets: ["Support client", "Qualification commerciale", "Automatisation"],
          status: "Live",
        },
        {
          tag: "03",
          icon: "globe",
          title: "Sites IA",
          desc: "Sites web et landing pages créatifs et intégrés à l'IA. Design premium, mobile-first. À partir de 490 € en 5–7 jours.",
          bullets: ["Mobile-first", "SSL · CDN", "5–7 jours"],
          status: "Live",
        },
        {
          tag: "04",
          icon: "cloud",
          title: "CloudOps",
          desc: "Serveurs, CI/CD, Docker, monitoring 24/7, SSL, DNS, CDN. AWS, GCP, Hetzner, OVH — entièrement géré.",
          bullets: ["CI/CD · Docker", "Monitoring 24/7", "Backups + patches"],
          status: "Live",
        },
        {
          tag: "05",
          icon: "cart",
          title: "CommerceAI",
          desc: "Commerce en ligne propulsé par l'IA. Pricing dynamique, recommandations, prévision de demande. NLP arabe & français.",
          bullets: ["Pricing dynamique", "Recommandations", "NLP AR · FR"],
          status: "Live",
        },
        {
          tag: "06",
          icon: "factory",
          title: "IndustryMind",
          desc: "Maintenance prédictive pour industriels. Intégration OPC-UA / SCADA. Anticipez les pannes avant qu'elles n'arrivent.",
          bullets: ["OPC-UA · SCADA", "IA prédictive", "Industrie 4.0"],
          status: "Bientôt",
        },
        {
          tag: "07 · Nouveau",
          icon: "router",
          title: "AI Router",
          desc: "Réduisez vos coûts IA de 30 à 70 %. Routage intelligent entre Claude, GPT, Gemini et modèles locaux — remplacement direct des endpoints compatibles OpenAI, avec failover automatique et plafonds budgétaires par fonctionnalité.",
          bullets: ["30–70 % d'économies", "Failover auto", "Compatible OpenAI"],
          status: "Live",
        },
      ],
    },
    cases: {
      eyebrow: "Cas d'usage",
      title: "Une solution pour chaque étape de votre croissance.",
      items: [
        {
          tag: "Laboratoires médicaux",
          title: "LabMind AI en production",
          problem:
            "Les laboratoires passaient plus de 30h/semaine à suivre les réactifs manuellement, avec ruptures fréquentes sur les lignes HbA1c, TSH et CRP.",
          solution:
            "Connexion des analyseurs via HL7/ASTM, logique FEFO, prédiction de rupture à J-7 et commandes fournisseurs automatisées — conforme ISO 15189.",
          metric: "J-7",
          metricLabel: "fenêtre de prédiction",
        },
        {
          tag: "PME & E-commerce",
          title: "AgentFlow sur WhatsApp",
          problem:
            "Équipes support submergées par les commandes, remboursements et questions produits en trois langues — délais à plus de 36h.",
          solution:
            "Un agent IA sur-mesure sur WhatsApp + e-mail + CRM, entraîné sur le catalogue et les politiques. En production en deux semaines, multilingue EN/FR/AR.",
          metric: "20+ h",
          metricLabel: "récupérées chaque semaine",
        },
        {
          tag: "Startups IA & SaaS",
          title: "AI Router réduit les factures LLM",
          problem:
            "Une équipe SaaS dépensait 9k$/mois sur un seul fournisseur LLM premium — chaque requête, simple ou complexe, atterrissait sur le modèle le plus cher.",
          solution:
            "AI Router route chaque prompt vers le modèle le moins cher qui respecte la qualité, avec failover automatique entre Claude, GPT, Gemini et un Llama 3 local. Endpoint compatible OpenAI — zéro refactor.",
          metric: "47%",
          metricLabel: "de réduction · 4 218 $ économisés le mois dernier",
        },
      ],
    },
    process: {
      eyebrow: "Comment on procède",
      title: "De la découverte au déploiement en quatre étapes.",
      steps: [
        { n: "01", title: "Découverte", desc: "Appel gratuit de 30 min. On cartographie vos workflows et identifions les leviers." },
        { n: "02", title: "Construction", desc: "Agents, intégrations et pipelines sur votre stack — observabilité et sécurité incluses." },
        { n: "03", title: "Déploiement", desc: "En production en 5–7 jours pour les sites, 2–3 semaines pour les agents IA. Équipe formée." },
        { n: "04", title: "Scale", desc: "Optimisation mensuelle, nouveaux agents, nouvelles victoires. Manager dédié sur Enterprise." },
      ],
    },
    testimonials: {
      eyebrow: "Paroles de clients",
      title: "Des opérateurs qui ont expédié — dans leurs propres mots.",
      note: "Noms anonymisés à la demande des clients. Secteur et région conservés.",
      items: [
        {
          quote:
            "LabMind AI a éliminé nos ruptures de réactifs. L'équipe a compris l'ISO 15189 dès le premier jour et a livré un outil que nos biologistes utilisent vraiment chaque matin.",
          role: "Directeur de laboratoire",
          company: "Laboratoire d'analyses médicales · Maroc",
          initials: "DL",
        },
        {
          quote:
            "AgentFlow a fait passer notre WhatsApp du chaos au pilotage automatique. 70% des demandes clients résolues sans qu'on touche à la boîte de réception. Deux semaines du kickoff au lancement.",
          role: "Fondateur",
          company: "Marque e-commerce · Maroc",
          initials: "EC",
        },
        {
          quote:
            "On brûlait 9k$/mois chez un seul fournisseur LLM. AI Router a fait passer ça à 4,8k$ dès le premier mois — même qualité, zéro modif de code. Le dashboard à lui seul vaut l'abonnement.",
          role: "CTO",
          company: "Startup SaaS · Afrique du Nord",
          initials: "CT",
        },
        {
          quote:
            "On avait engagé trois agences avant MAIF. Les seuls à mesurer le succès en heures économisées, pas en slides. L'équipe est devenue un vrai partenaire d'ingénierie interne.",
          role: "COO",
          company: "Société de logistique · Afrique de l'Ouest",
          initials: "CO",
        },
      ],
    },
    pricing: {
      eyebrow: "Investissement",
      title: "Tarification transparente. Sans surprise.",
      toggle: { oneTime: "Forfait unique", monthly: "Mensuel · sans frais d'installation" },
      tiers: [
        {
          name: "Starter",
          subtitle: "Site IA / Chatbot",
          price: "490 €",
          priceMonthly: "149 €/mois",
          cadence: "unique",
          cadenceMonthly: "+ 99 €/mois · sans engagement",
          desc: "Landing page ou chatbot IA, livré en 5–7 jours.",
          features: [
            "Landing page ou chatbot IA",
            "Mobile-first + SSL",
            "Livré en 5–7 jours",
            "Support 30 jours offert",
          ],
          cta: "Commencer",
        },
        {
          name: "Agent IA sur-mesure",
          subtitle: "Le plus populaire",
          price: "1 500 €",
          priceMonthly: "399 €/mois",
          cadence: "jusqu'à 4 000 € · unique",
          cadenceMonthly: "+ 299 €/mois · sans frais de setup",
          desc: "Agent IA entièrement personnalisé, intégré à votre stack.",
          features: [
            "Agent IA entièrement custom",
            "CRM + WhatsApp + e-mail",
            "Livré en 2–3 semaines",
            "3 mois de support",
          ],
          cta: "Lancer ce projet",
          highlight: true,
        },
        {
          name: "Enterprise",
          subtitle: "Transformation IA complète",
          price: "5 000 €",
          priceMonthly: "899 €/mois",
          cadence: "jusqu'à 15 000 € · unique",
          cadenceMonthly: "+ 599 €/mois · build inclus",
          desc: "Plusieurs agents, automatisation de bout en bout, manager dédié.",
          features: [
            "Plusieurs agents IA",
            "Automatisation de bout en bout",
            "Formation équipe + docs",
            "Manager dédié + SLA",
          ],
          cta: "Discutons-en",
        },
      ],
      cloudOps: {
        name: "CloudOps",
        subtitle: "Cloud & DevOps",
        price: "299 €",
        priceMonthly: "199 €/mois",
        cadence: "setup · + 149 €/mois géré",
        cadenceMonthly: "infra entièrement gérée",
        features: ["Serveur + DNS + SSL + CDN", "CI/CD + Docker", "Monitoring 24/7 + alertes", "Backups + patches mensuels"],
        cta: "Déployer l'infra",
      },
      aiRouter: {
        name: "AI Router",
        subtitle: "Optimisation des coûts LLM",
        price: "49 $",
        priceMonthly: "199 $/mois",
        cadence: "/mois Starter · 500K tokens",
        cadenceMonthly: "Pro · 5M tokens · fournisseurs illimités",
        features: [
          "Routage intelligent entre LLM",
          "Failover auto · 99,99% uptime",
          "Plafonds + alertes en temps réel",
          "Compatible OpenAI · zéro refactor",
        ],
        cta: "Essai gratuit",
      },
    },
    faq: {
      eyebrow: "Questions fréquentes",
      title: "Tout ce que vous vouliez demander avant l'appel.",
      items: [
        {
          q: "À quelle vitesse pouvez-vous livrer concrètement ?",
          a: "Landing pages et sites IA : 5 à 7 jours. Agents IA sur-mesure (AgentFlow) : 2 à 3 semaines du kickoff au lancement. Transformations enterprise : 6 à 10 semaines selon le périmètre. On s'engage par écrit sur les dates avant de démarrer.",
        },
        {
          q: "Travaillez-vous avec des clients non-marocains ?",
          a: "Oui. Notre siège est au Maroc et on sert des entreprises africaines et des clients du monde entier. On travaille en anglais, français et arabe, et notre équipe est à l'aise avec les fuseaux UE et US.",
        },
        {
          q: "Et si l'IA hallucine ou fait une erreur ?",
          a: "Chaque agent qu'on livre a des garde-fous : seuils de confiance, retrieval grounding, escalades humaines et logs d'audit complets. Les workflows critiques (médical, financier) exigent toujours une validation humaine avant action. On mesure les taux d'erreur jusqu'à ce qu'ils soient sous votre seuil de tolérance.",
        },
        {
          q: "Comment gérez-vous la vie privée et le RGPD ?",
          a: "Vos données vous appartiennent. AI Router ne stocke jamais le contenu des prompts ni les réponses (métadonnées uniquement, chiffrées en transit). Pour LabMind AI, on livre des pistes d'audit ISO 15189. On signe des DPA et offrons l'hébergement UE sur demande. Aucun entraînement de modèles externes sur vos données — jamais.",
        },
        {
          q: "Puis-je annuler mon abonnement mensuel ?",
          a: "Oui, à tout moment. Les abonnements mensuels s'arrêtent à la fin du cycle de facturation, sans questions. AI Router inclut un essai gratuit de 14 jours et une garantie de remboursement de 30 jours si on ne réduit pas vos coûts d'au moins 20%.",
        },
        {
          q: "Quelles langues vos agents IA supportent-ils ?",
          a: "Nativement : anglais, français et arabe (darija inclus). Pour d'autres langues, on s'appuie sur les modèles multilingues Claude, GPT-4 ou Gemini — traduction et transcription incluses dans AgentFlow et CommerceAI.",
        },
      ],
    },
    lead: {
      eyebrow: "Appel découverte gratuit",
      title: "Votre prochaine grande idée mérite l'IA.",
      desc: "Parlez-nous de votre activité. On vous renvoie les trois automatisations à plus fort levier — sans pitch commercial.",
      name: "Nom complet",
      email: "E-mail professionnel",
      business: "Quel produit vous intéresse ?",
      message: "Parlez-nous de vos opérations (facultatif)",
      submit: "Réserver un appel gratuit",
      submitting: "Envoi…",
      success: "Demande reçue. Nous revenons vers vous sous 24 heures.",
      error: "Une erreur est survenue. Réessayez.",
      privacy: "On respecte votre boîte mail. Pas de spam.",
    },
    about: {
      eyebrow: "Qui sommes-nous",
      title: "Built in Africa for the world.",
      body:
        "Moroccan AI Factory est un studio basé au Maroc, au service des entreprises africaines et des clients du monde entier. On conçoit des agents IA, applications et sites web qui font la différence — pour les labos, les PME et les équipes en croissance, sur le continent et au-delà. On mesure le succès en heures économisées, pas en slides.",
      stats: [
        { v: "Maroc", l: "Siège" },
        { v: "Afrique & Monde", l: "Portée" },
        { v: "EN · FR · AR", l: "Langues" },
      ],
      founder: "Fondateur",
    },
    finalCta: {
      title_pre: "Votre prochaine grande idée mérite",
      title_accent: "l'IA",
      title_post: ".",
      desc: "Nous transformons les défis business complexes en solutions IA puissantes. Pas de templates. Pas de raccourcis. Que des résultats.",
      primary: "Lancer un projet",
      secondary: "Nous écrire sur WhatsApp",
    },
    footer: {
      tagline: "Agents IA, apps et sites web — construits au Maroc pour le monde.",
      rights: "Tous droits réservés.",
      links: {
        product: "Produits",
        company: "Société",
        legal: "Contact",
      },
    },
  },
  ar: {
    nav: {
      services: "الخدمات",
      cases: "حالات الاستخدام",
      pricing: "الأسعار",
      faq: "الأسئلة الشائعة",
      about: "من نحن",
      cta: "ابدأ مشروعك",
    },
    hero: {
      eyebrow: "المغرب · إفريقيا · العالم · تأسست 2026",
      title_pre: "نحوّل أفكاركم إلى",
      title_accent: "واقع",
      title_post: "مدعوم بالذكاء الاصطناعي",
      subtitle:
        "نصمّم ونطوّر وننشر وكلاء ذكاء اصطناعي وتطبيقات ومواقع للشركات الطموحة في المغرب وإفريقيا. لا قوالب، لا اختصارات — فقط نتائج.",
      primary: "ابدأ مشروعك",
      secondary: "تواصل عبر واتساب",
      micro: "مكالمة استكشافية مجانية لمدة 30 دقيقة · بدون أي التزام",
      live: {
        label: "مباشر",
        value: "+218 س",
        caption: "ساعة مؤتمتة هذا الشهر عبر وكلائنا",
        responseLabel: "متوسط الردّ",
        responseValue: "1.4 ث",
      },
    },
    proof: {
      label: "أرقام تُحدث الفرق",
      items: [
        { value: "7", label: "منتجات مُطلقة" },
        { value: "+20 س", label: "ساعة موفّرة أسبوعياً" },
        { value: "47%", label: "تخفيض تكلفة الذكاء الاصطناعي" },
        { value: "$4.2k", label: "متوسط التوفير الشهري" },
        { value: "100%", label: "حلول مخصّصة" },
      ],
      strip: "LABMIND AI · AGENTFLOW · AI ROUTER · CLOUDOPS · COMMERCEAI · INDUSTRYMIND",
    },
    ps: {
      eyebrow: "التحوّل",
      title: "عمليّاتك لم تُصمَّم لعام 2026.",
      problems: {
        title: "أين يضيع وقت فريقك",
        items: [
          "متابعة يدوية للمخزون والكواشف",
          "تأخّر دعم العملاء بالعربية والفرنسية والإنجليزية",
          "أدوات متفرّقة — CRM وواتساب والبريد والجداول",
          "قرارات متأخّرة بسبب غياب البيانات في الوقت الفعلي",
        ],
      },
      solutions: {
        title: "ما نقوم بتركيبه",
        items: [
          "وكلاء ذكاء اصطناعي يُنفّذون لا يكتفون بالردّ",
          "تكامل أجهزة المختبرات عبر HL7 / ASTM",
          "أتمتة شاملة لمنظومتك التقنية",
          "نماذج مُهيّأة للواقع الإفريقي",
        ],
      },
    },
    services: {
      eyebrow: "ما نبنيه",
      title: "حلول ذكاء اصطناعي شاملة، تنمو مع أعمالك.",
      items: [
        { tag: "01 · المنتج الرئيسي", icon: "lab", title: "LabMind AI", desc: "إدارة الكواشف للمختبرات الطبية في الوقت الفعلي. تكامل مع المحلّلات عبر HL7/ASTM. توقّع نفاد المخزون قبل 7 أيام. متوافق مع ISO 15189.", bullets: ["HL7 / ASTM", "ISO 15189", "FEFO · طلب تلقائي"], status: "Live" },
        { tag: "02", icon: "bot", title: "AgentFlow", desc: "وكلاء ذكاء اصطناعي مخصّصون لدعم العملاء وتأهيل العملاء المحتملين وأتمتة العمليات. CRM + واتساب + بريد. أكثر من 20 ساعة موفّرة أسبوعياً.", bullets: ["دعم العملاء", "تأهيل العملاء", "أتمتة العمليات"], status: "Live" },
        { tag: "03", icon: "globe", title: "مواقع الذكاء الاصطناعي", desc: "مواقع وصفحات هبوط إبداعية مدمَجة بالذكاء الاصطناعي. تصميم راقٍ، يُولي الجوال الأولوية، وسريع. بدءاً من 490 € خلال 5–7 أيام.", bullets: ["الجوال أولاً", "SSL · CDN", "5–7 أيام"], status: "Live" },
        { tag: "04", icon: "cloud", title: "CloudOps", desc: "اختيار الخوادم والنشر التلقائي والمراقبة على مدار الساعة. AWS، GCP، Hetzner، OVH — بإدارة كاملة.", bullets: ["CI/CD · Docker", "مراقبة 24/7", "نسخ احتياطية"], status: "Live" },
        { tag: "05", icon: "cart", title: "CommerceAI", desc: "تجارة إلكترونية مدعومة بالذكاء الاصطناعي. تسعير ديناميكي، توصيات، وتنبّؤ بالطلب. معالجة لغوية بالعربية والفرنسية.", bullets: ["تسعير ديناميكي", "توصيات", "AR · FR NLP"], status: "Live" },
        { tag: "06", icon: "factory", title: "IndustryMind", desc: "صيانة تنبّؤية للصناعات. تكامل OPC-UA / SCADA. منع الأعطال قبل وقوعها.", bullets: ["OPC-UA · SCADA", "ذكاء تنبّؤي", "الصناعة 4.0"], status: "قريباً" },
        { tag: "07 · جديد", icon: "router", title: "AI Router", desc: "خفّض تكاليف الذكاء الاصطناعي بنسبة 30 إلى 70%. توجيه ذكي بين Claude وGPT وGemini والنماذج المحلية — متوافق مع OpenAI، مع تبديل تلقائي عند الفشل.", bullets: ["خفض 30–70%", "تبديل تلقائي", "متوافق مع OpenAI"], status: "Live" },
      ],
    },
    cases: {
      eyebrow: "حالات الاستخدام",
      title: "حلول لكلّ مرحلة من نموّ أعمالك.",
      items: [
        { tag: "المختبرات الطبية", title: "LabMind AI في الإنتاج", problem: "كان الفنّيون يقضون أكثر من 30 ساعة أسبوعياً في متابعة الكواشف يدوياً مع نفاد متكرّر في خطوط HbA1c وTSH وCRP.", solution: "ربطنا المحلّلات عبر HL7/ASTM، ونشرنا منطق FEFO وتوقّع النفاد قبل 7 أيام، وأتمتنا الطلبات — متوافق مع ISO 15189.", metric: "7 أيام", metricLabel: "نافذة التوقّع" },
        { tag: "PME والتجارة الإلكترونية", title: "AgentFlow على واتساب", problem: "فرق الدعم مُثقلة بالطلبات وطلبات الاسترجاع بثلاث لغات — أوقات الردّ تجاوزت 36 ساعة.", solution: "وكيل ذكاء اصطناعي مخصّص على واتساب والبريد وCRM، مدرَّب على الكتالوج والسياسات. في الإنتاج خلال أسبوعين، متعدّد اللغات.", metric: "+20 س", metricLabel: "موفّرة كلّ أسبوع" },
        { tag: "الشركات الناشئة وSaaS", title: "AI Router يخفّض فواتير LLM", problem: "كان فريق SaaS ينفق 9000 دولار شهرياً على مزوّد LLM واحد — كلّ طلب يصل إلى نفس النموذج المكلِف.", solution: "AI Router يوجّه كلّ طلب إلى أقلّ نموذج تكلفةً يحقّق الجودة، مع تبديل تلقائي بين Claude وGPT وGemini وLlama 3 محلّي. نقطة وصول متوافقة مع OpenAI — بدون أيّ تعديل.", metric: "47%", metricLabel: "تخفيض في التكلفة · 4218 $ موفّرة الشهر الماضي" },
      ],
    },
    process: {
      eyebrow: "كيف نعمل",
      title: "من الاكتشاف إلى النشر في أربع خطوات.",
      steps: [
        { n: "01", title: "الاكتشاف", desc: "مكالمة مجّانية لمدة 30 دقيقة. نرسم تدفّقات عملك ونحدّد الأتمتة الأعلى أثراً." },
        { n: "02", title: "البناء", desc: "وكلاء وتكاملات وأنابيب على منظومتك — مع ضمان الرصد والأمان." },
        { n: "03", title: "النشر", desc: "في الإنتاج خلال 5–7 أيام للمواقع، 2–3 أسابيع لوكلاء AI. تدريب الفريق وتسليم التوثيق." },
        { n: "04", title: "التوسّع", desc: "تحسين شهري ووكلاء جدد ومكاسب جديدة. مدير مخصّص واتفاقية مستوى خدمة لخطة Enterprise." },
      ],
    },
    testimonials: {
      eyebrow: "أصوات العملاء",
      title: "مشغّلون أنجزوا فعلياً — بكلماتهم.",
      note: "أُخفيت الأسماء بطلب من العملاء. القطاع والمنطقة محفوظان للسياق.",
      items: [
        { quote: "LabMind AI أنهى نفاد الكواشف لدينا. الفريق أتقن ISO 15189 من اليوم الأوّل وسلّم أداة يستعملها بيولوجيّونا كلّ صباح.", role: "مدير مختبر", company: "مختبر تحاليل طبية · المغرب", initials: "LD" },
        { quote: "AgentFlow حوّل واتساب من فوضى إلى طيّار آلي. 70% من استفسارات العملاء تُحلّ دون أن يلمس فريقنا الصندوق. أسبوعان من الانطلاق إلى الإنتاج.", role: "مؤسّس", company: "علامة تجارة إلكترونية · المغرب", initials: "EC" },
        { quote: "كنّا ننفق 9 آلاف دولار شهرياً على مزوّد LLM واحد. AI Router خفّضها إلى 4.8 آلاف في أوّل 30 يوماً — نفس الجودة، صفر تعديلات. لوحة التحكّم وحدها تستحقّ الاشتراك.", role: "مدير تقنية", company: "ستارت‑أب SaaS · شمال إفريقيا", initials: "CT" },
        { quote: "وظّفنا ثلاث وكالات قبل MAIF. الوحيدون الذين قاسوا النجاح بالساعات الموفّرة وليس بالعروض التقديمية. الفريق يبدو وكأنّه شريك هندسي داخلي.", role: "مدير عمليات", company: "شركة لوجستيات · غرب إفريقيا", initials: "CO" },
      ],
    },
    pricing: {
      eyebrow: "الاستثمار",
      title: "تسعير شفّاف. لا مفاجآت.",
      toggle: { oneTime: "دفعة واحدة", monthly: "شهرياً · بدون رسوم إعداد" },
      tiers: [
        { name: "Starter", subtitle: "موقع AI / شات‑بوت", price: "490 €", priceMonthly: "149 €/شهر", cadence: "دفعة واحدة", cadenceMonthly: "+ 99 €/شهر · بدون التزام", desc: "صفحة هبوط أو شات‑بوت AI، يُسلَّم خلال 5–7 أيام.", features: ["صفحة أو شات‑بوت", "Mobile-first + SSL", "تسليم خلال 5–7 أيام", "30 يوم دعم مجاني"], cta: "ابدأ" },
        { name: "وكيل AI مخصّص", subtitle: "الأكثر طلباً", price: "1,500 €", priceMonthly: "399 €/شهر", cadence: "حتى 4,000 € · دفعة واحدة", cadenceMonthly: "+ 299 €/شهر · بدون رسوم إعداد", desc: "وكيل AI مخصّص بالكامل ومدمج في منظومتك.", features: ["وكيل مخصّص بالكامل", "CRM + واتساب + بريد", "تسليم خلال 2–3 أسابيع", "3 أشهر دعم"], cta: "أطلق المشروع", highlight: true },
        { name: "Enterprise", subtitle: "تحوّل AI كامل", price: "5,000 €", priceMonthly: "899 €/شهر", cadence: "حتى 15,000 € · دفعة واحدة", cadenceMonthly: "+ 599 €/شهر · البناء مشمول", desc: "وكلاء متعدّدون، أتمتة شاملة، مدير مخصّص.", features: ["وكلاء AI متعدّدون", "أتمتة شاملة", "تدريب الفريق + توثيق", "مدير مخصّص + SLA"], cta: "تحدّث معنا" },
      ],
      cloudOps: { name: "CloudOps", subtitle: "Cloud & DevOps", price: "299 €", priceMonthly: "199 €/شهر", cadence: "إعداد · + 149 €/شهر مُدار", cadenceMonthly: "بنية تحتيّة بإدارة كاملة", features: ["خادم + DNS + SSL + CDN", "CI/CD + Docker", "مراقبة 24/7 + تنبيهات", "نسخ احتياطية شهرية"], cta: "انشر البنية" },
      aiRouter: { name: "AI Router", subtitle: "تحسين تكاليف LLM", price: "$49", priceMonthly: "$199/شهر", cadence: "/شهر Starter · 500K توكن", cadenceMonthly: "Pro · 5M توكن · مزوّدون غير محدودين", features: ["توجيه ذكي بين النماذج", "تبديل تلقائي · 99.99% توفّر", "حدود وتنبيهات فوريّة", "متوافق مع OpenAI · بدون تعديل"], cta: "تجربة مجّانية" },
    },
    faq: {
      eyebrow: "الأسئلة الشائعة",
      title: "كل ما أردتم سؤاله قبل حجز المكالمة.",
      items: [
        { q: "ما السرعة الفعلية للتسليم؟", a: "صفحات الهبوط ومواقع AI: 5 إلى 7 أيام. وكلاء AI مخصّصون (AgentFlow): 2 إلى 3 أسابيع من الانطلاق إلى الإنتاج. تحوّلات Enterprise: 6 إلى 10 أسابيع. نلتزم بالمواعيد كتابياً قبل البدء." },
        { q: "هل تعملون مع عملاء غير مغاربة؟", a: "نعم. مقرّنا المغرب ونخدم شركات إفريقيّة وعملاء حول العالم. نعمل بالعربية والفرنسية والإنجليزية، وفريقنا مرتاح مع ساعات أوروبا والولايات المتّحدة." },
        { q: "ماذا لو هلوس الذكاء الاصطناعي أو أخطأ؟", a: "كلّ وكيل نسلّمه يحمل ضوابط: حدود ثقة، تأصيل بالاسترجاع، تصعيد بشري، وسجلّات تدقيق كاملة. التدفّقات الحرجة (طبية، مالية) تتطلّب موافقة بشرية قبل التنفيذ." },
        { q: "كيف تعالجون الخصوصية وGDPR؟", a: "بياناتك ملك لك. AI Router لا يخزّن محتوى المطالبات أو الردود. لـ LabMind AI، نسلّم سجلّات تدقيق ISO 15189. نوقّع DPA ونوفّر إقامة بيانات في الاتّحاد الأوروبي عند الطلب." },
        { q: "هل يمكنني إلغاء اشتراكي الشهري؟", a: "نعم، في أيّ وقت. الاشتراكات الشهرية تنتهي مع نهاية دورة الفوترة. AI Router يشمل تجربة 14 يوماً وضمان استرجاع 30 يوماً إذا لم نخفّض تكاليفك بنسبة 20% على الأقلّ." },
        { q: "ما اللغات التي يدعمها وكلاؤكم؟", a: "أصلاً: العربية (الدارجة) والفرنسية والإنجليزية. للّغات الأخرى نعتمد على نماذج Claude وGPT-4 وGemini متعدّدة اللغات — الترجمة والنسخ مشمولان." },
      ],
    },
    lead: {
      eyebrow: "مكالمة استكشافية مجّانية",
      title: "فكرتك الكبيرة القادمة تستحقّ الذكاء الاصطناعي.",
      desc: "أخبرنا عن أعمالك. سنرسل لك أعلى ثلاث أتمتات أثراً — بدون عرض تجاري.",
      name: "الاسم الكامل",
      email: "البريد المهني",
      business: "أيّ منتج يهمّك؟",
      message: "أخبرنا عن عمليّاتك (اختياري)",
      submit: "احجز مكالمة مجّانية",
      submitting: "يتمّ الإرسال…",
      success: "تمّ استلام الطلب. سنتواصل معك خلال 24 ساعة.",
      error: "حدث خطأ. حاول مجدّداً.",
      privacy: "نحترم بريدك. لا رسائل مزعجة أبداً.",
    },
    about: {
      eyebrow: "من نحن",
      title: "بُني في إفريقيا للعالم.",
      body:
        "Moroccan AI Factory ستوديو مقرّه المغرب يخدم الشركات الإفريقية والعملاء حول العالم. نصمّم وكلاء ذكاء اصطناعي وتطبيقات ومواقع تُحدث الفرق — للمختبرات وPME والفرق النامية. نقيس النجاح بالساعات الموفّرة لا بالعروض.",
      stats: [
        { v: "المغرب", l: "المقرّ" },
        { v: "إفريقيا والعالم", l: "النطاق" },
        { v: "EN · FR · AR", l: "اللغات" },
      ],
      founder: "المؤسّس",
    },
    finalCta: {
      title_pre: "فكرتك الكبيرة القادمة تستحقّ",
      title_accent: "الذكاء الاصطناعي",
      title_post: ".",
      desc: "نحوّل تحدّيات الأعمال المعقّدة إلى حلول ذكاء اصطناعي قويّة. لا قوالب. لا اختصارات. فقط نتائج.",
      primary: "ابدأ مشروعك",
      secondary: "راسلنا على واتساب",
    },
    footer: {
      tagline: "وكلاء ذكاء اصطناعي وتطبيقات ومواقع — تُبنى في المغرب للعالم.",
      rights: "جميع الحقوق محفوظة.",
      links: { product: "المنتجات", company: "الشركة", legal: "تواصل" },
    },
  },
};

// Real contact channels from maif.ma
export const WHATSAPP_URL = "https://wa.me/33624848265";
export const EMAIL = "contact@moroccanaifactory.com";
export const INSTAGRAM_URL = "https://instagram.com/moroccan.ai.factory";
// Kept for backward compatibility — points to WhatsApp (no Calendly on maif.ma)
export const CALENDLY_URL = WHATSAPP_URL;

export const BUSINESS_TYPES_EN = [
  "LabMind AI — Healthcare",
  "AgentFlow — Automation",
  "AI Router — LLM cost optimization",
  "AI Website / Landing Page",
  "CloudOps — Infrastructure",
  "CommerceAI — E-commerce",
  "Full AI Transformation",
  "Other",
];
export const BUSINESS_TYPES_FR = [
  "LabMind AI — Santé",
  "AgentFlow — Automatisation",
  "AI Router — Optimisation coûts LLM",
  "Site IA / Landing Page",
  "CloudOps — Infrastructure",
  "CommerceAI — E-commerce",
  "Transformation IA complète",
  "Autre",
];
